import ReactGA from "react-ga4";

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

/**
 * Custom Experiment Parameter Tracker for A/B Testing Funnels
 * Uses custom params to avoid conflicts with UTM parameters used for ads
 *
 * Custom Parameters:
 * - exp_name: Experiment name (e.g., "bundle_test", "checkout_v2")
 * - exp_variant: Variant identifier (e.g., "variant_a", "variant_b", "control")
 * - exp_id: Optional experiment ID for tracking
 *
 * Example URL:
 * /CheckoutPage/?exp_name=bundle_test&exp_variant=variant_a
 */

export interface ExperimentParameters {
  experimentName: string | null;
  experimentVariant: string | null;
  experimentId: string | null;
}

class ExperimentTracker {
  private urlParams: URLSearchParams;
  private experimentParams: ExperimentParameters;

  constructor() {
    this.urlParams = new URLSearchParams(window.location.search);
    this.experimentParams = this.extractExperimentParams();
    
    // If no URL params but sessionStorage has experiment data, load from session
    if (!this.isInExperiment()) {
      const sessionParams = ExperimentTracker.loadFromSession();
      if (sessionParams.experimentName) {
        this.experimentParams = sessionParams;
      }
    }
    
    // Save to sessionStorage if we have experiment parameters
    if (this.isInExperiment()) {
      this.saveToSession();
    }
  }

  /**
   * Extract experiment parameters from URL
   */
  private extractExperimentParams(): ExperimentParameters {
    return {
      experimentName: this.urlParams.get("exp_name"),
      experimentVariant: this.urlParams.get("exp_variant"),
      experimentId: this.urlParams.get("exp_id"),
    };
  }

  /**
   * Get all experiment parameters
   */
  getParams(): ExperimentParameters {
    return this.experimentParams;
  }

  /**
   * Check if user is in an experiment
   */
  isInExperiment(): boolean {
    return this.experimentParams.experimentName !== null;
  }

  /**
   * Get current variant
   */
  getVariant(): string | null {
    return this.experimentParams.experimentVariant;
  }

  /**
   * Get experiment name
   */
  getExperimentName(): string | null {
    return this.experimentParams.experimentName;
  }

  /**
   * Build custom page path with experiment parameters
   * This creates unique paths for funnel tracking in GA4
   */
  buildCustomPagePath(basePath: string): string {
    if (!this.isInExperiment()) {
      return basePath; // No experiment, return original path
    }

    const params = new URLSearchParams();

    if (this.experimentParams.experimentName) {
      params.set("exp_name", this.experimentParams.experimentName);
    }
    if (this.experimentParams.experimentVariant) {
      params.set("exp_variant", this.experimentParams.experimentVariant);
    }
    if (this.experimentParams.experimentId) {
      params.set("exp_id", this.experimentParams.experimentId);
    }

    return `${basePath}?${params.toString()}`;
  }

  /**
   * Track page view with experiment parameters
   * ONLY tracks if user is in an experiment
   */
  trackPageView(pagePath: string, pageTitle?: string): void {
    if (!this.isInExperiment()) {
      return;
    }

    const customPagePath = this.buildCustomPagePath(pagePath);

    ReactGA.send({
      hitType: "pageview",
      page: customPagePath,
      title: pageTitle || document.title,
    });
  }

  /**
   * Save experiment parameters to sessionStorage
   */
  saveToSession(): void {
    if (this.experimentParams.experimentName) {
      sessionStorage.setItem("exp_name", this.experimentParams.experimentName);
    }
    if (this.experimentParams.experimentVariant) {
      sessionStorage.setItem("exp_variant", this.experimentParams.experimentVariant);
    }
    if (this.experimentParams.experimentId) {
      sessionStorage.setItem("exp_id", this.experimentParams.experimentId);
    }
  }

  /**
   * Load from session (useful for pages without URL params)
   */
  static loadFromSession(): ExperimentParameters {
    return {
      experimentName: sessionStorage.getItem("exp_name"),
      experimentVariant: sessionStorage.getItem("exp_variant"),
      experimentId: sessionStorage.getItem("exp_id"),
    };
  }

  /**
   * Get experiment query string
   */
  toQueryString(): string {
    if (!this.isInExperiment()) return "";

    const params = new URLSearchParams();
    if (this.experimentParams.experimentName) {
      params.set("exp_name", this.experimentParams.experimentName);
    }
    if (this.experimentParams.experimentVariant) {
      params.set("exp_variant", this.experimentParams.experimentVariant);
    }
    if (this.experimentParams.experimentId) {
      params.set("exp_id", this.experimentParams.experimentId);
    }
    return params.toString();
  }

  /**
   * Check if user is in specific experiment
   */
  isInExperimentNamed(experimentName: string): boolean {
    return this.experimentParams.experimentName === experimentName;
  }

  /**
   * Check if user is in specific variant
   */
  isInVariant(variantName: string): boolean {
    return this.experimentParams.experimentVariant === variantName;
  }

  /**
   * Add experiment parameters to any GA4 event data
   */
  addExperimentParams(eventData: any): any {
    if (!this.isInExperiment()) {
      return eventData;
    }

    // Set experiment parameters as user properties
    this.setExperimentUserProperties();

    // Send experiment parameters directly to gtag to ensure they reach GA4
    this.sendExperimentEvent(eventData.action, eventData);
    
    // Return the enhanced event data for ReactGA4 with snake_case for GA4
    return {
      ...eventData,
      exp_name: this.experimentParams.experimentName,
      exp_variant: this.experimentParams.experimentVariant,
      exp_id: this.experimentParams.experimentId,
    };
  }

  /**
   * Send event with experiment parameters (replaces ReactGA.event when in experiment)
   * Use this method instead of ReactGA.event to avoid duplication
   */
  sendEvent(eventData: any): void {
    if (this.isInExperiment()) {
      // Send directly to gtag with experiment parameters
      this.sendExperimentEvent(eventData.action, eventData);
    } else {
      // Use ReactGA4 for non-experiment events
      ReactGA.event(eventData);
    }
  }

  /**
   * Send experiment parameters directly to GA4 using gtag
   * This ensures experiment parameters reach GA4 even if ReactGA4 doesn't send them
   */
  private sendExperimentEvent(eventName: string, eventData: any): void {
    if (!this.isInExperiment()) {
      return;
    }

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        event_category: eventData.category,
        event_label: eventData.label,
        exp_name: this.experimentParams.experimentName,
        exp_variant: this.experimentParams.experimentVariant,
        exp_id: this.experimentParams.experimentId,
      });
    }
  }

  /**
   * Set experiment parameters as GA4 user properties
   * User properties persist across sessions and help analyze user behavior
   * Call this method once when initializing the experiment tracker
   */
  setExperimentUserProperties(): void {
    if (!this.isInExperiment()) {
      return;
    }

    if (typeof window !== 'undefined' && window.gtag) {
      const userProperties: Record<string, string | null> = {};

      if (this.experimentParams.experimentName) {
        userProperties.exp_name = this.experimentParams.experimentName;
      }
      if (this.experimentParams.experimentVariant) {
        userProperties.exp_variant = this.experimentParams.experimentVariant;
      }
      if (this.experimentParams.experimentId) {
        userProperties.exp_id = this.experimentParams.experimentId;
      }

      window.gtag('set', 'user_properties', userProperties);
    }
  }

  /**
   * Debug log - only logs in development mode
   */
  debug(): void {
    if (process.env.NODE_ENV === 'development') {
      console.group("🧪 Experiment Tracker");
      console.log("In Experiment:", this.isInExperiment());
      console.log("Experiment Name:", this.experimentParams.experimentName);
      console.log("Variant:", this.experimentParams.experimentVariant);
      console.log("Experiment ID:", this.experimentParams.experimentId);
      console.log("Query String:", this.toQueryString());
      console.log("Custom Path:", this.buildCustomPagePath(window.location.pathname));
      console.groupEnd();
    }
  }
}

// Export singleton
export const experimentTracker = new ExperimentTracker();

export default ExperimentTracker;
